// frontend/src/components/handbook/HandbookChecklist.js
import React, { useState, useEffect } from "react";
import {
  CheckSquare,
  Square,
  HelpCircle,
  RotateCcw,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import {
  getItemState,
  setItemState,
  resetChapter,
  getChapterProgress,
} from "../../utils/handbookProgress";

const ChecklistItem = ({ chapterSlug, item, onToggle }) => {
  const [checked, setChecked] = useState(() =>
    getItemState(chapterSlug, item.key)
  );
  const [hintOpen, setHintOpen] = useState(false);
  const [highlight, setHighlight] = useState(false);

  // Highlight if URL hash matches this item
  useEffect(() => {
    if (window.location.hash === `#${item.key}`) {
      setHighlight(true);
      setTimeout(() => setHighlight(false), 2200);
      // Scroll into view shortly after mount
      const el = document.getElementById(item.key);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 150);
      }
    }
  }, [item.key]);

  const toggle = () => {
    const next = !checked;
    setChecked(next);
    setItemState(chapterSlug, item.key, next);
    onToggle?.();
  };

  return (
    <li
      id={item.key}
      className={`border-b border-gray-100 last:border-b-0 transition-colors ${
        highlight ? "bg-yellow-50" : ""
      }`}
    >
      <div className="flex items-start gap-2 py-3 px-2">
        <button
          type="button"
          onClick={toggle}
          aria-pressed={checked}
          aria-label={
            checked ? "Erledigt — Klick zum Zurücksetzen" : "Als erledigt markieren"
          }
          className="flex-shrink-0 w-11 h-11 flex items-center justify-center rounded-md hover:bg-gray-100 transition-colors active:scale-95"
        >
          {checked ? (
            <CheckSquare className="w-6 h-6 text-[#84BD00]" />
          ) : (
            <Square className="w-6 h-6 text-gray-400" />
          )}
        </button>

        <div className="flex-1 min-w-0 pt-2">
          <div
            onClick={toggle}
            className={`cursor-pointer text-base leading-snug ${
              checked ? "text-gray-400 line-through" : "text-[#002855]"
            }`}
          >
            {item.text}
          </div>
          {hintOpen && item.hint && (
            <div className="mt-2 text-sm text-gray-600 bg-blue-50 border-l-4 border-[#00A3E0] px-3 py-2 rounded-r">
              {item.hint}
            </div>
          )}
        </div>

        {item.hint && (
          <button
            type="button"
            onClick={() => setHintOpen((v) => !v)}
            aria-label={hintOpen ? "Hinweis verbergen" : "Hinweis anzeigen"}
            className="flex-shrink-0 w-11 h-11 flex items-center justify-center rounded-md hover:bg-gray-100 transition-colors text-[#00A3E0]"
          >
            <HelpCircle className="w-5 h-5" />
          </button>
        )}
      </div>
    </li>
  );
};

const ChecklistGroup = ({ chapterSlug, checklist, defaultOpen, onToggle }) => {
  const [open, setOpen] = useState(defaultOpen);

  // If a hash points to an item inside this group, force-open
  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (!hash) return;
    if (checklist.items?.some((i) => i.key === hash)) {
      setOpen(true);
    }
  }, [checklist.items]);

  // Live progress for this group
  let done = 0;
  let total = checklist.items?.length || 0;
  for (const item of checklist.items || []) {
    if (getItemState(chapterSlug, item.key)) done += 1;
  }
  const allDone = total > 0 && done === total;

  const groupAnchorId = `cl-${slugifyTitle(checklist.title || "")}`;

  return (
    <div
      id={groupAnchorId}
      className="mb-4 bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm scroll-mt-32"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-gray-50 hover:bg-gray-100 border-b border-gray-200 transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0">
          {open ? (
            <ChevronDown className="w-5 h-5 text-[#00A3E0] flex-shrink-0" />
          ) : (
            <ChevronRight className="w-5 h-5 text-[#00A3E0] flex-shrink-0" />
          )}
          <h3 className="text-base font-semibold text-[#002855] text-left truncate">
            {checklist.title || "Checkliste"}
          </h3>
        </div>
        <span
          className={`text-xs flex-shrink-0 font-medium ${
            allDone ? "text-[#84BD00]" : "text-gray-500"
          }`}
        >
          {done}/{total}
          {allDone ? " ✓" : ""}
        </span>
      </button>
      {open && (
        <ul className="px-2">
          {checklist.items?.map((item) => (
            <ChecklistItem
              key={item.key}
              chapterSlug={chapterSlug}
              item={item}
              onToggle={onToggle}
            />
          ))}
        </ul>
      )}
    </div>
  );
};

const slugifyTitle = (s) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "") || "checkliste";

const HandbookChecklist = ({ chapterSlug, checklists, onProgressChange }) => {
  const [, forceTick] = useState(0);

  if (!Array.isArray(checklists) || checklists.length === 0) {
    return null;
  }

  const handleToggle = () => {
    forceTick((n) => n + 1);
    onProgressChange?.();
  };

  const handleResetAll = () => {
    if (!window.confirm("Alle Häkchen in diesem Kapitel zurücksetzen?")) return;
    resetChapter(chapterSlug, checklists);
    forceTick((n) => n + 1);
    onProgressChange?.();
  };

  const { done, total } = getChapterProgress(chapterSlug, checklists);

  return (
    <section className="mt-8 mb-4">
      <div className="flex items-center justify-between mb-3 pb-2 border-b-2 border-[#84BD00]">
        <h2 className="text-xl md:text-2xl font-semibold text-[#002855]">
          Checklisten
          {total > 0 && (
            <span className="ml-2 text-sm font-normal text-gray-500">
              ({done}/{total})
            </span>
          )}
        </h2>
        {total > 0 && (
          <button
            type="button"
            onClick={handleResetAll}
            className="text-xs text-gray-500 hover:text-[#002855] flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-100"
          >
            <RotateCcw className="w-3 h-3" />
            Zurücksetzen
          </button>
        )}
      </div>

      {checklists.map((checklist, idx) => (
        <ChecklistGroup
          key={`${slugifyTitle(checklist.title || "")}-${idx}`}
          chapterSlug={chapterSlug}
          checklist={checklist}
          defaultOpen={idx === 0}
          onToggle={handleToggle}
        />
      ))}
    </section>
  );
};

export default HandbookChecklist;
